import { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';

const AdminPage = () => {
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});

  const fetchReservations = async () => {
    try {
      const response = await axios.get('https://reservation-backend-634485963533.us-west1.run.app/api/admin/reservations');
      setReservations(response.data);
    } catch (err) {
      console.error('Error fetching reservations:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReservations();
  }, []);

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this reservation?')) {
      try {
        await axios.delete(`https://reservation-backend-634485963533.us-west1.run.app/api/admin/reservations/${id}`);
        fetchReservations();
      } catch (err) {
        console.error('Error deleting reservation:', err);
      }
    }
  };

  const handleEdit = (reservation) => {
    setEditingId(reservation.id);
    setEditForm({
      date: reservation.reservation_date.split('T')[0],
      time: reservation.reservation_time,
      partySize: reservation.party_size,
      status: reservation.status,
      specialRequests: reservation.special_requests || ''
    });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditForm({});
  };

  const handleSaveEdit = async (id) => {
    try {
      await axios.put(`https://reservation-backend-634485963533.us-west1.run.app/api/admin/reservations/${id}`, editForm);
      setEditingId(null);
      setEditForm({});
      fetchReservations();
      alert('Reservation updated successfully!');
    } catch (err) {
      console.error('Error updating reservation:', err);
      alert('Failed to update reservation');
    }
  };

  const handleSendEmail = async (reservation) => {
    try {
      await axios.post('https://reservation-backend-634485963533.us-west1.run.app/api/send-email', {
        email: reservation.email,
        name: reservation.name,
        date: reservation.reservation_date,
        time: reservation.reservation_time
      });
      alert('Confirmation email sent!');
    } catch (err) {
      console.error('Error sending email:', err);
      alert('Failed to send email');
    }
  };

  const filteredReservations = reservations.filter(res => {
    const matchesSearch = res.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      res.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      res.phone.includes(searchTerm);
    
    const matchesDate = !dateFilter || res.reservation_date.split('T')[0] === dateFilter;
    
    return matchesSearch && matchesDate;
  });

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-neutral-600 dark:text-neutral-400">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-black py-12 px-4">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="text-4xl font-bold text-neutral-800 dark:text-neutral-200 mb-8">
            Admin Dashboard - All Reservations
          </h1>

          {/* Search and Filter Bar */}
          <div className="mb-6 flex gap-4 flex-wrap">
            <input
              type="text"
              placeholder="Search by name, email, or phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="flex-1 min-w-[200px] px-4 py-2 rounded-lg border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-900 text-neutral-900 dark:text-neutral-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <input
              type="date"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="px-4 py-2 rounded-lg border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-900 text-neutral-900 dark:text-neutral-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {dateFilter && (
              <button
                onClick={() => setDateFilter('')}
                className="px-4 py-2 rounded-lg bg-neutral-200 dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-300 dark:hover:bg-neutral-700 transition-colors"
              >
                Clear Date
              </button>
            )}
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <div className="bg-white dark:bg-black rounded-xl p-6 shadow-lg border border-neutral-200 dark:border-white/[0.1]">
              <p className="text-sm text-neutral-500 dark:text-neutral-400">Total Reservations</p>
              <p className="text-3xl font-bold text-neutral-800 dark:text-neutral-200">{reservations.length}</p>
            </div>
            <div className="bg-white dark:bg-black rounded-xl p-6 shadow-lg border border-neutral-200 dark:border-white/[0.1]">
              <p className="text-sm text-neutral-500 dark:text-neutral-400">Confirmed</p>
              <p className="text-3xl font-bold text-green-600 dark:text-green-400">
                {reservations.filter(r => r.status === 'confirmed').length}
              </p>
            </div>
            <div className="bg-white dark:bg-black rounded-xl p-6 shadow-lg border border-neutral-200 dark:border-white/[0.1]">
              <p className="text-sm text-neutral-500 dark:text-neutral-400">Search Results</p>
              <p className="text-3xl font-bold text-blue-600 dark:text-blue-400">{filteredReservations.length}</p>
            </div>
          </div>

          {/* Reservations Table */}
          {filteredReservations.length === 0 ? (
            <p className="text-neutral-600 dark:text-neutral-400 text-center py-12">No reservations found.</p>
          ) : (
            <div className="bg-white dark:bg-black rounded-xl shadow-lg border border-neutral-200 dark:border-white/[0.1] overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-neutral-50 dark:bg-neutral-900">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                        ID
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                        Name
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                        Email
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                        Phone
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                        Date
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                        Time
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                        Special Requests
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-neutral-200 dark:divide-neutral-800">
                    {filteredReservations.map((reservation) => (
                      <motion.tr
                        key={reservation.id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="hover:bg-neutral-50 dark:hover:bg-neutral-900/50 transition-colors"
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900 dark:text-neutral-100">
                          {reservation.id}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-neutral-900 dark:text-neutral-100">
                          {reservation.name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-700 dark:text-neutral-300">
                          {reservation.email}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-700 dark:text-neutral-300">
                          {reservation.phone}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-700 dark:text-neutral-300">
                          {editingId === reservation.id ? (
                            <input
                              type="date"
                              value={editForm.date}
                              onChange={(e) => setEditForm({...editForm, date: e.target.value})}
                              className="px-2 py-1 rounded border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-800"
                            />
                          ) : (
                            new Date(reservation.reservation_date).toLocaleDateString()
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-700 dark:text-neutral-300">
                          {editingId === reservation.id ? (
                            <input
                              type="time"
                              value={editForm.time}
                              onChange={(e) => setEditForm({...editForm, time: e.target.value})}
                              className="px-2 py-1 rounded border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-800"
                            />
                          ) : (
                            reservation.reservation_time
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {editingId === reservation.id ? (
                            <select
                              value={editForm.status}
                              onChange={(e) => setEditForm({...editForm, status: e.target.value})}
                              className="px-2 py-1 rounded border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-800"
                            >
                              <option value="confirmed">Confirmed</option>
                              <option value="pending">Pending</option>
                              <option value="cancelled">Cancelled</option>
                            </select>
                          ) : (
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                              reservation.status === 'confirmed' 
                                ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400'
                                : reservation.status === 'cancelled'
                                ? 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-400'
                                : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-400'
                            }`}>
                              {reservation.status}
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm text-neutral-700 dark:text-neutral-300 max-w-xs">
                          {editingId === reservation.id ? (
                            <input
                              type="text"
                              value={editForm.specialRequests}
                              onChange={(e) => setEditForm({...editForm, specialRequests: e.target.value})}
                              className="w-full px-2 py-1 rounded border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-800"
                            />
                          ) : (
                            <span className="truncate block">{reservation.special_requests || '-'}</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm space-x-2">
                          {editingId === reservation.id ? (
                            <>
                              <button
                                onClick={() => handleSaveEdit(reservation.id)}
                                className="px-3 py-1 rounded-lg bg-green-500 hover:bg-green-600 text-white font-medium transition-colors duration-200"
                              >
                                Save
                              </button>
                              <button
                                onClick={handleCancelEdit}
                                className="px-3 py-1 rounded-lg bg-gray-500 hover:bg-gray-600 text-white font-medium transition-colors duration-200"
                              >
                                Cancel
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                onClick={() => handleEdit(reservation)}
                                className="px-3 py-1 rounded-lg bg-blue-500 hover:bg-blue-600 text-white font-medium transition-colors duration-200"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleSendEmail(reservation)}
                                className="px-3 py-1 rounded-lg bg-purple-500 hover:bg-purple-600 text-white font-medium transition-colors duration-200"
                              >
                                Email
                              </button>
                              <button
                                onClick={() => handleDelete(reservation.id)}
                                className="px-3 py-1 rounded-lg bg-red-500 hover:bg-red-600 text-white font-medium transition-colors duration-200"
                              >
                                Delete
                              </button>
                            </>
                          )}
                        </td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default AdminPage;
